import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { FF_LIGHT_MODE } from '@/consts/featureFlags';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useSystemThemeMode } from '@/hooks/useSystemThemeMode';
import { useUserStore } from '@/state/user.state';
import {
  clearStoredThemeMode,
  DEFAULT_THEME_MODE,
  readStoredThemeMode,
  resolveThemeMode,
  writeStoredThemeMode,
} from '@/utils/themeMode.utils';
import type { ThemeMode } from '@figma/fpl-themes';
import type { ThemePreference } from '@/types/auth.types';

/**
 * The color mode the app is currently rendering in.
 *
 * Defaults to `dark` rather than `undefined` so that a consumer rendered outside the provider —
 * a portal mounted before `WeaveThemeProvider`, or a test rendering a component in isolation —
 * degrades to today's appearance instead of throwing or rendering unthemed.
 */
export const LocalThemeModeContext = createContext<(mode: ThemePreference) => void>(() => {});

const ThemePreferenceContext = createContext<ThemePreference>(DEFAULT_THEME_MODE);

export const ThemeModeContext = createContext<ThemeMode>(DEFAULT_THEME_MODE);

/**
 * Resolves the app's color mode once and shares it with every descendant.
 *
 * Mounted inside `WeaveThemeProvider` so the feature flag and the user preference are each read a
 * single time, mirroring how `FplMigrationProvider` shares `fpl-migration`. Consumers that need to
 * re-assert the mode inside a portal read it from here rather than hardcoding `mode="dark"`.
 *
 * @param isFigmaEmbedOverride - Mirrors the prop of the same name on `WeaveThemeProvider`. A caller
 * that mounts the provider *above* `FigmaEmbedModeProvider` reads the embed context's `false`
 * default, which would let the embed resolve to light mode; passing the override keeps it dark.
 */
export function ThemeModeProvider({
  children,
  isFigmaEmbedOverride,
}: {
  children: ReactNode;
  isFigmaEmbedOverride?: boolean;
}) {
  const { value: isLightModeEnabled } = useFeatureFlag(FF_LIGHT_MODE);
  const contextIsFigmaEmbed = useIsFigmaEmbedMode();
  const isFigmaEmbed = isFigmaEmbedOverride ?? contextIsFigmaEmbed;
  const preferredMode = useUserStore((state) => state.user?.preferences?.theme);

  // Storage seeds first paint; explicit guest choices update this local fallback.
  // A signed-in user’s server preference remains authoritative.
  const [storedMode, setStoredMode] = useState<ThemePreference | null>(() => readStoredThemeMode());

  const preference = preferredMode ?? storedMode ?? DEFAULT_THEME_MODE;
  const systemMode = useSystemThemeMode();

  const mode = useMemo(
    () =>
      resolveThemeMode({
        isLightModeFlagEnabled: isLightModeEnabled === true,
        isFigmaEmbed,
        preferredMode,
        storedMode,
        systemMode,
      }),
    [isLightModeEnabled, isFigmaEmbed, preferredMode, storedMode, systemMode],
  );

  // Keep the mirror in step with the server preference. Without this, a user who switches modes on
  // one device would get a first-paint flash of the old mode on every other device, because the
  // mirror would still hold the stale value until they toggled again here.
  //
  // When the flag is explicitly off, drop the mirror instead: surfaces that read storage without
  // being able to check the flag (see `StandalonePageLoader`) would otherwise keep rendering a
  // stale `light` after the flag was turned off. `undefined` means "not resolved yet" and is left
  // alone so a slow flag response doesn't clear a valid preference.
  useEffect(() => {
    if (isLightModeEnabled === false) {
      clearStoredThemeMode();
      return;
    }
    if (isLightModeEnabled === true && preferredMode) {
      writeStoredThemeMode(preferredMode);
    }
  }, [isLightModeEnabled, preferredMode]);

  return (
    <LocalThemeModeContext.Provider value={setStoredMode}>
      <ThemePreferenceContext.Provider value={preference}>
        <ThemeModeContext.Provider value={mode}>{children}</ThemeModeContext.Provider>
      </ThemePreferenceContext.Provider>
    </LocalThemeModeContext.Provider>
  );
}

/**
 * The color mode the app is rendering in. Returns `dark` outside a `ThemeModeProvider`.
 *
 * Use this for portalled content that needs to re-apply the app theme, e.g.
 * `<ColorTheme brand="weave" mode={useThemeMode()}>`. Do **not** use it for inverse surfaces such
 * as tooltips: those are dark in both modes by token (`--color-bg-tooltip` is `grey-900` in the
 * light and dark blocks alike) and must stay pinned.
 */
export function useThemeMode(): ThemeMode {
  return useContext(ThemeModeContext);
}

/**
 * Whether the light-mode toggle should be offered at all. Separate from `useThemeMode` because the
 * flag can be on while the user is still in dark mode, and because embed mode hides the toggle
 * without changing the resolved mode.
 */
export function useIsThemeModeSelectable(): boolean {
  const { value: isLightModeEnabled } = useFeatureFlag(FF_LIGHT_MODE);
  const isFigmaEmbed = useIsFigmaEmbedMode();
  return isLightModeEnabled === true && !isFigmaEmbed;
}

/** The saved choice, kept separate from the resolved Light/Dark rendering mode. */
export function useThemePreference(): ThemePreference {
  return useContext(ThemePreferenceContext);
}
